import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tvShowService, TVShowsQueryParams, EpisodesQueryParams, SeasonsQueryParams } from '@/services/tvShowService';
import { CreateTVShowRequest, CreateEpisodeRequest, CreateSeasonRequest, BulkEpisodePreviewRequest, EpisodeExternalIdReference } from '@/types/model';

export const tvShowKeys = {
  all: ['tvShows'] as const,
  lists: () => [...tvShowKeys.all, 'list'] as const,
  list: (params: TVShowsQueryParams) => [...tvShowKeys.lists(), params] as const,
  details: () => [...tvShowKeys.all, 'detail'] as const,
  detail: (id: string) => [...tvShowKeys.details(), id] as const,
  episodes: () => [...tvShowKeys.all, 'episodes'] as const,
  episodesList: (params: EpisodesQueryParams) => [...tvShowKeys.episodes(), 'list', params] as const,
  episodesForShow: (tvShowId: string) => [...tvShowKeys.episodes(), 'show', tvShowId] as const,
  episodeDetail: (id: string) => [...tvShowKeys.episodes(), 'detail', id] as const,
  seasons: () => [...tvShowKeys.all, 'seasons'] as const,
  seasonsList: (params: SeasonsQueryParams) => [...tvShowKeys.seasons(), 'list', params] as const,
  seasonsForShow: (tvShowId: string) => [...tvShowKeys.seasons(), 'show', tvShowId] as const,
  seasonDetail: (id: string) => [...tvShowKeys.seasons(), 'detail', id] as const,
};

// TV Show hooks
export const useTVShows = (params: TVShowsQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: tvShowKeys.list(params),
    queryFn: () => tvShowService.getTVShows(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useTVShowList = (params: TVShowsQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: tvShowKeys.list({ ...params, _view: 'list' }),
    queryFn: () => tvShowService.getTVShowList(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useTVShow = (id: string) => {
  return useQuery({
    queryKey: tvShowKeys.detail(id),
    queryFn: () => tvShowService.getTVShowById(id),
    enabled: !!id,
  });
};

export const useCreateTVShow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tvShowData: CreateTVShowRequest) =>
      tvShowService.createTVShow(tvShowData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.lists() });
    },
  });
};

export const useUpdateTVShow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateTVShowRequest> }) =>
      tvShowService.updateTVShow(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: tvShowKeys.lists() });
    },
  });
};

export const useLinkTVShowToTmdb = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, tmdbId }: { id: string; tmdbId: string }) =>
      tvShowService.linkTVShowToTmdb(id, tmdbId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: tvShowKeys.lists() });
    },
  });
};

export const useDeleteTVShow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tvShowService.deleteTVShow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.lists() });
    },
  });
};

export const useToggleTVShowStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      tvShowService.toggleTVShowStatus(id, active),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: tvShowKeys.lists() });
    },
  });
};

export const usePromoteTVShow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tvShowService.promoteTVShow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.lists() });
    },
  });
};

export const useImportTVShow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { id: string; token: string }) =>
      tvShowService.importTVShow(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.lists() });
    },
  });
};

// Episode hooks
export const useEpisodes = (params: EpisodesQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: tvShowKeys.episodesList(params),
    queryFn: () => tvShowService.getEpisodes(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useEpisodesByTVShow = (tvShowId: string, params: TVShowsQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: tvShowKeys.episodesForShow(tvShowId),
    queryFn: () => tvShowService.getEpisodesByTVShowId(tvShowId, params),
    enabled: !!tvShowId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useEpisode = (id: string) => {
  return useQuery({
    queryKey: tvShowKeys.episodeDetail(id),
    queryFn: () => tvShowService.getEpisodeById(id),
    enabled: !!id,
  });
};

export const useCreateEpisode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (episodeData: CreateEpisodeRequest) =>
      tvShowService.createEpisode(episodeData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.episodes() });
      queryClient.invalidateQueries({ queryKey: tvShowKeys.episodesForShow(variables.tvShowId) });
      if (variables.tvShowId) {
        queryClient.invalidateQueries({ queryKey: tvShowKeys.detail(variables.tvShowId) });
      }
    },
  });
};

export const usePreviewBulkEpisodes = () => {
  return useMutation({
    mutationFn: ({ tvShowId, request }: { tvShowId: string; request: BulkEpisodePreviewRequest }) =>
      tvShowService.previewBulkEpisodes(tvShowId, request),
  });
};

export const useCreateBulkEpisodes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tvShowId, episodes }: { tvShowId: string; episodes: CreateEpisodeRequest[] }) =>
      tvShowService.createBulkEpisodes(tvShowId, episodes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.episodes() });
      queryClient.invalidateQueries({ queryKey: tvShowKeys.episodesForShow(variables.tvShowId) });
      queryClient.invalidateQueries({ queryKey: tvShowKeys.detail(variables.tvShowId) });
    },
  });
};

export const useValidateBulkEpisodeLinks = () => {
  return useMutation({
    mutationFn: ({ tvShowId, urls }: { tvShowId: string; urls: string[] }) =>
      tvShowService.validateBulkEpisodeLinks(tvShowId, urls),
  });
};

export const useBulkEpisodeExternalIds = () => {
  return useMutation({
    mutationFn: ({
      tvShowId,
      tmdbId,
      episodes,
    }: {
      tvShowId: string;
      tmdbId: string;
      episodes: EpisodeExternalIdReference[];
    }) => tvShowService.getBulkEpisodeExternalIds(tvShowId, tmdbId, episodes),
  });
};

export const useUpdateEpisode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateEpisodeRequest> }) =>
      tvShowService.updateEpisode(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.episodeDetail(variables.id) });
      queryClient.invalidateQueries({ queryKey: tvShowKeys.episodes() });
      if (variables.data.tvShowId) {
        queryClient.invalidateQueries({ queryKey: tvShowKeys.episodesForShow(variables.data.tvShowId) });
      }
    },
  });
};

export const useDeleteEpisode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({tvShowId, episodeId}:{tvShowId: string, episodeId: string}) => tvShowService.deleteEpisode(tvShowId, episodeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.episodes() });
    },
  });
};

export const useDeleteEpisodes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tvShowId, episodeIds }: { tvShowId: string; episodeIds: string[] }) =>
      tvShowService.deleteEpisodes(tvShowId, episodeIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.episodes() });
      queryClient.invalidateQueries({ queryKey: tvShowKeys.episodesForShow(variables.tvShowId) });
      queryClient.invalidateQueries({ queryKey: tvShowKeys.detail(variables.tvShowId) });
    },
  });
};

export const useToggleEpisodeStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tvShowId, episodeId, active }: { episodeId: string; active: boolean, tvShowId: string }) =>
      tvShowService.toggleEpisodeStatus(tvShowId, episodeId, active),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.episodeDetail(variables.episodeId) });
      queryClient.invalidateQueries({ queryKey: tvShowKeys.episodes() });
    },
  });
};

// Season hooks
export const useSeasons = (params: SeasonsQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: tvShowKeys.seasonsList(params),
    queryFn: () => tvShowService.getSeasons(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useSeasonsByTVShow = (tvShowId: string, params: TVShowsQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: tvShowKeys.seasonsForShow(tvShowId),
    queryFn: () => tvShowService.getSeasonsByTVShowId(tvShowId, params),
    enabled: !!tvShowId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useSeason = (id: string) => {
  return useQuery({
    queryKey: tvShowKeys.seasonDetail(id),
    queryFn: () => tvShowService.getSeasonById(id),
    enabled: !!id,
  });
};

export const useCreateSeason = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (seasonData: CreateSeasonRequest) =>
      tvShowService.createSeason(seasonData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.seasons() });
      queryClient.invalidateQueries({ queryKey: tvShowKeys.seasonsForShow(variables.tvShowId) });
      queryClient.invalidateQueries({ queryKey: tvShowKeys.detail(variables.tvShowId) });
    },
  });
};

export const useUpdateSeason = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateSeasonRequest> }) =>
      tvShowService.updateSeason(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.seasonDetail(variables.id) });
      queryClient.invalidateQueries({ queryKey: tvShowKeys.seasons() });
      if (variables.data.tvShowId) {
        queryClient.invalidateQueries({ queryKey: tvShowKeys.seasonsForShow(variables.data.tvShowId) });
      }
    },
  });
};

export const useDeleteSeason = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tvShowService.deleteSeason(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.seasons() });
    },
  });
};

export const useToggleSeasonStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      tvShowService.toggleSeasonStatus(id, active),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tvShowKeys.seasonDetail(variables.id) });
      queryClient.invalidateQueries({ queryKey: tvShowKeys.seasons() });
    },
  });
};
